import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui';
import type { UploadEntry, UploadQueue } from './uploads';

const HIDE_AFTER_MS = 3000;

/** Upload queue of the board (spec §10.1 step 7): progress per file, retry and cancel; hides once all is sent. */
export function UploadPanel({ queue }: { queue: Pick<UploadQueue, 'entries' | 'subscribe' | 'retry' | 'cancel'> }) {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<UploadEntry[]>(() => queue.entries());
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    setEntries(queue.entries());
    return queue.subscribe(() => setEntries(queue.entries()));
  }, [queue]);

  const allDone = entries.length > 0 && entries.every((e) => e.state === 'done');
  useEffect(() => {
    if (!allDone) {
      setHidden(false);
      return;
    }
    const timer = setTimeout(() => setHidden(true), HIDE_AFTER_MS);
    return () => clearTimeout(timer);
  }, [allDone]);

  if (entries.length === 0 || hidden) return null;
  return (
    <section
      aria-label={t('upload.title')}
      className="absolute bottom-4 left-4 z-20 w-72 max-w-[calc(100%-2rem)] rounded-lg border border-border bg-surface p-3 font-sans text-sm text-fg shadow-md"
    >
      <h2 className="mb-2 font-display font-bold">{t('upload.title')}</h2>
      <ul className="flex max-h-64 flex-col gap-2 overflow-y-auto">
        {entries.map((e) => {
          const pct = e.state === 'done' ? 100 : e.bytesTotal ? Math.round((100 * e.bytesDone) / e.bytesTotal) : 0;
          const message = e.state === 'failed' ? 'upload.failed' : e.state === 'quota' ? 'upload.quota' : e.state === 'forbidden' ? 'upload.forbidden' : null;
          return (
            <li key={e.assetId} className="flex flex-col gap-1">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate">{e.name}</span>
                <span className="flex shrink-0 gap-1">
                  {(e.state === 'failed' || e.state === 'quota') && (
                    <Button variant="ghost" size="sm" aria-label={t('upload.retryFile', { name: e.name })} onClick={() => queue.retry(e.assetId)}>
                      {t('upload.retry')}
                    </Button>
                  )}
                  {e.state !== 'done' && (
                    <Button variant="ghost" size="sm" aria-label={t('upload.cancelFile', { name: e.name })} onClick={() => queue.cancel(e.assetId)}>
                      {t('upload.cancel')}
                    </Button>
                  )}
                </span>
              </div>
              <div role="progressbar" aria-label={e.name} aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} className="h-1 overflow-hidden rounded bg-border">
                <div className="h-1 bg-accent transition-[width]" style={{ width: `${pct}%` }} />
              </div>
              {message && <p className="text-xs text-fg-muted">{t(message)}</p>}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
